import Core from '../Core'
import { getType } from '../initializers/Utils'

import { PublicError } from '../Error'
import { WRONG_PLATFORM } from '../ErrorCodes'
import { WRONG_CONSTRUCTOR } from '../ErrorReasons'

import * as settings from '../settings'

export default class {
  constructor () {
    this._isInitialized = false
  }
  isInitialized () {
    return this._isInitialized
  }
  configure () {
    return Core.publicWrapper({
      name: 'configure',
      arguments: arguments,
      parameters: ['region', 'environment', 'onSuccess', 'onFailure', 'onComplete'],
      validations: [
        { type: 'string', name: 'region', presence: true },
        { type: 'string', name: 'environment', presence: true },
      ],
    }, async function wrapped (params) {
      const env = params.environment === 'prod-uat' ? 'prodUat' : params.environment
      const setting = settings[params.region.toLowerCase()][env]
      Core.set('__REGION_CODE__', params.region)

      Core.logger._setLogLevel(setting.LOG_LEVEL)
      Core.set('__API_BASE_URL__', setting.API_BASE_URL)
      Core.set('__S3_PROXY_BASE_URL__', setting.S3_PROXY_BASE_URL)
    })
  }

  init (clientId, clientSecret, redirectUrl, channel) {
    return Core.init({ clientId, clientSecret, redirectUrl, channel }).then(() => {
      this._isInitialized = true
    })
  }

  onError (callback) {
    return Core.execute('Api', 'registerErrorCallback', { callback })
  }

  isAuthorized () {
    return Core.execute('Api', 'isAuthorized')
  }
  isAuthenticated () {
    return this.isAuthorized()
  }

  _publicWrapperWithoutParameter (name, args) {
    return Core.publicWrapper({
      name,
      arguments: args,
      parameters: ['onSuccess', 'onFailure', 'onComplete'],
    })
  }

  authorizeUserWithIdentifier () {
    return Core.publicWrapper({
      name: 'authorizeUserWithIdentifier',
      arguments: arguments,
      parameters: ['userExternalId', 'userEmail', 'onSuccess', 'onFailure', 'onComplete', 'customFields'],
      validations: [
        { type: 'string', name: 'userExternalId', presence: true },
        { type: 'string', name: 'userEmail' },
        { type: 'object', name: 'customFields' },
      ],
    })
  }

  authorizeUserWithToken () {
    return Core.publicWrapper({
      name: 'authorizeUserWithToken',
      arguments: arguments,
      parameters: ['token', 'onSuccess', 'onFailure', 'onComplete'],
      validations: [
        { name: 'token', type: 'string', presence: true },
      ],
    })
  }

  // deprecated in favor of `uploadWithFiles`
  uploadWithFileInput () {
    Core.logger.warn(`(DeprecationWarning) using method "uploadWithFileInput" is deprecated - use "uploadWithFiles" instead`)

    return Core.publicWrapper({
      name: 'uploadWithFileInput',
      arguments: arguments,
      parameters: ['eans', 'inputId', 'onProgress', 'onSuccess', 'onFailure', 'onComplete', 'customFields', 'offerIdentifiers'],
      validations: [
        { type: 'array',  name: 'eans' },
        { type: 'string', name: 'inputId' },
        { type: 'object', name: 'customFields' },
        { type: 'array',  name: 'offerIdentifiers' },
      ],
    }, async function wrapped (params) {
      if (typeof window === 'undefined') {
        throw new PublicError(WRONG_PLATFORM, 'this method can only be used in browser')
      }

      const domInput = global.document.getElementById(params.inputId)
      Core.validateParam('uploadWithFileInput', domInput,
        { name: 'inputId', presence: true, message: 'no input element #' + params.inputId })
      Core.validateParam('uploadWithFileInput', domInput.files,
        { name: 'inputId', presence: true, message: 'this input #' + params.inputId + ' is not a file input' })
      Core.validateParam('uploadWithFileInput', domInput.files.length,
        { name: 'inputId', notEqual: 0, message: 'no file in this input #' + params.inputId })

      return Core.createReceiptAndUploadSubpictures(domInput.files, params)
    })
  }

  uploadWithFiles () {
    return Core.publicWrapper({
      name: 'uploadWithFiles',
      arguments: arguments,
      parameters: ['eans', 'files', 'onProgress', 'onSuccess', 'onFailure', 'onComplete', 'customFields', 'offerIdentifiers'],
      validations: [
        { type: 'array',  name: 'eans' },
        { type: 'object', name: 'customFields' },
        { type: 'array',  name: 'offerIdentifiers' },
      ],
    }, async function wrapped (params) {
      if (params.files instanceof FileList) {
        // nothing to do
      } else if (params.files instanceof Array) {
        const len = params.files.length
        for (let i = 0; i < len; i = i + 1) {
          if (!(params.files[i] instanceof File)) {
            Core.throwInvalidParameter(
              `parameter "files[${i}]" must be a File`,
              { parameter: `files[${i}]`, reason: WRONG_CONSTRUCTOR },
            )
          }
        }
      } else {
        Core.throwInvalidParameter(
          'parameter "files" must be an instance of FileList or an array of File',
          { parameter: 'files', reason: WRONG_CONSTRUCTOR },
        )
      }

      Core.validateParam('uploadWithFiles', params.files, { name: 'files', notEmpty: true })

      return Core.createReceiptAndUploadSubpictures(params.files, params)
    })
  }

  // deprecated in favor of `uploadWithFiles`
  uploadWithIframe () {
    Core.logger.warn(`(DeprecationWarning) using method "uploadWithIframe" is deprecated - use "uploadWithFiles" instead`)

    return Core.publicWrapper({
      name: 'uploadWithIframe',
      arguments: arguments,
      parameters: ['eans', 'iframeId', 'onSuccess', 'onFailure', 'onComplete', 'customFields'],
      validations: [
        { name: 'customFields', type: 'object' },
        { name: 'eans', undef: false },
        { name: 'iframeId', presence: true, message: 'no HTML ID for the iframe has been provided' },
        { name: 'iframeId', type: 'string' },
        { name: 'iframeId', undef: false, message: 'no HTML ID for the iframe has been provided' },
      ],
    }, async function wrapped (params) {
      if (typeof window === 'undefined') {
        throw new PublicError(WRONG_PLATFORM, 'this method can only be used in browser')
      }

      const { eans, iframeId, customFields } = params

      const eansType = getType(eans)
      if (
        (typeof eans !== 'undefined') &&
        (eans !== null) &&
        (eans !== true) &&
        (eans !== false) &&
        (eansType !== 'array')
      ) {
        const message = 'parameter "eans" can not be ' + (eansType === 'object' ? 'an ' : 'a ') + eansType
        Core.throwInvalidParameter(message, { parameter: 'eans', reason: 'wrong_type' })
      }

      return Core.execute('Api', 'initIframe', { eans, iframeId, customFields })
    })
  }

  getPendingReceipts () {
    return this._publicWrapperWithoutParameter('getPendingReceipts', arguments)
  }

  signup () {
    return Core.publicWrapper({
      name: 'signup',
      arguments: arguments,
      parameters: ['email', 'password', 'onSuccess', 'onFailure', 'onComplete', 'activationCode', 'firstName'],
      validations: [
        { type: 'string', name: 'email', presence: true },
        { type: 'string', name: 'password', presence: true },
        { type: 'string', name: 'activationCode' },
        { type: 'string', name: 'firstName' },
      ],
    })
  }

  login () {
    return Core.publicWrapper({
      name: 'login',
      arguments: arguments,
      parameters: ['email', 'password', 'onSuccess', 'onFailure', 'onComplete'],
      validations: [
        { type: 'string', name: 'email', presence: true },
        { type: 'string', name: 'password', presence: true },
      ],
    })
  }

  logout () {
    return this._publicWrapperWithoutParameter('logout', arguments)
  }

  resetPassword () {
    return Core.publicWrapper({
      name: 'resetPassword',
      arguments: arguments,
      parameters: ['email', 'onSuccess', 'onFailure', 'onComplete'],
      validations: [
        { name: 'email', type: 'string', presence: true },
      ],
    })
  }
}
